import React, { useEffect, useRef, useState } from 'react';

import { Button } from '../../../../components/Button/Button';
import {
  usePollTgQrLoginMutation,
  useStartTgQrLoginMutation,
} from '../../../../redux/services/TgSessionsApi';
import { type TgSession } from '../../../../redux/types/TgSessionsTypes';
import { getErrorText } from './helpers';
import styles from '../TgSessions.module.css';

const POLL_INTERVAL_MS = 3000;

interface QrLoginStepProps {
  sessionId: number;
  onStatus: (updated: TgSession) => void;
  onClose: () => void;
}

const QrLoginStep = ({ sessionId, onStatus, onClose }: QrLoginStepProps) => {
  const [startQr] = useStartTgQrLoginMutation();
  const [pollQr] = usePollTgQrLoginMutation();
  const [qrImage, setQrImage] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [attempt, setAttempt] = useState(0);
  const onStatusRef = useRef(onStatus);
  onStatusRef.current = onStatus;

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let request: ReturnType<typeof pollQr> | undefined;

    const handle = (updated: TgSession): boolean => {
      if (updated.status === 'QR_PENDING') {
        setQrImage(updated.qr_image || '');
        return true;
      }
      if (updated.status === 'ACTIVE' || updated.status === 'PASSWORD_REQUIRED') {
        onStatusRef.current(updated);
        return false;
      }
      setError('QR-код больше не действителен — запросите новый.');
      return false;
    };
    const poll = async () => {
      request = pollQr(sessionId);
      try {
        const updated = await request.unwrap();
        if (cancelled) return;
        if (handle(updated)) timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (requestError) {
        if (!cancelled) setError(getErrorText(requestError, 'Не удалось проверить вход по QR'));
      }
    };
    const start = async () => {
      request = startQr(sessionId);
      try {
        const started = await request.unwrap();
        if (cancelled) return;
        if (handle(started)) timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (requestError) {
        if (!cancelled) setError(getErrorText(requestError, 'Не удалось получить QR-код'));
      }
    };

    setError('');
    setQrImage('');
    start();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      request?.abort();
    };
  }, [sessionId, attempt, startQr, pollQr]);

  return (
    <>
      <p className={styles.stepText}>
        Откройте Telegram на телефоне с этим номером → Настройки → Устройства → Подключить
        устройство → наведите камеру на QR.
      </p>
      {qrImage ? (
        <img className={styles.qrImage} src={qrImage} alt={'QR-код для входа в Telegram'} />
      ) : (
        <div className={`${styles.qrImage} ${styles.qrPlaceholder}`}>
          {error ? 'QR-код недоступен' : 'Загружаем QR-код…'}
        </div>
      )}
      {error ? (
        <div className={`${styles.qrStatus} ${styles.bad}`}>{error}</div>
      ) : (
        <div className={`${styles.qrStatus} ${styles.warn}`}>Ожидаем сканирование…</div>
      )}
      {error && (
        <Button
          text={'Запросить новый QR'}
          type={'button'}
          filled={false}
          style={{ marginRight: '8px' }}
          action={() => setAttempt((value) => value + 1)}
        />
      )}
      <Button text={'Закрыть'} type={'button'} action={onClose} />
    </>
  );
};

export default QrLoginStep;
